// src/components/ProjectForm.js
import React, { useState, useEffect } from 'react';
import { auth, db } from '../firebase';
import { collection, onSnapshot, addDoc, serverTimestamp, query, orderBy, doc, updateDoc, deleteDoc } from 'firebase/firestore';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import Select from 'react-select';
import toast from 'react-hot-toast';
import { FaPlus, FaTrash, FaBook, FaClipboardList, FaSave } from 'react-icons/fa';

const labelStyle = "block text-sm font-medium text-gray-700 mb-1";
const inputStyle = "mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#18BC9C] focus:border-[#18BC9C] disabled:bg-gray-200 disabled:cursor-not-allowed";
const selectStyles = {
    control: (base, state) => ({ ...base, border: '1px solid #D1D5DB', boxShadow: 'none', '&:hover': { borderColor: '#18BC9C' }, minHeight: '42px', backgroundColor: state.isDisabled ? '#E5E7EB' : 'white' }),
    option: (styles, { isFocused }) => ({ ...styles, backgroundColor: isFocused ? '#18BC9C' : null, color: isFocused ? 'white' : 'black' }),
};

const STATUS_OPTIONS = ['Aprobado', 'En Diseño', 'En Producción', 'Instalación', 'Pendiente', 'Completado', 'Cancelado', 'Retrasado'];
// 🔥 CORRECCIÓN: Nos aseguramos de que el estado inicial esté limpio
const initialFormState = { prioridad: 'Normal', departamento: 'Madera', estado: 'Aprobado', personaContacto: '', vendedor: '', descripcion: '' };

function ProjectForm({ onSave, initialData, onCancel, usuario, vistaActual }) {
    const isReadOnly = usuario?.role === 'operario';
    const [nombreProyecto, setNombreProyecto] = useState('');
    const [formData, setFormData] = useState(initialFormState);
    const [ubicacion, setUbicacion] = useState('');
    const [selectedCliente, setSelectedCliente] = useState(null);
    const [fechaEntrega, setFechaEntrega] = useState(null);
    const [cronogramaInicio, setCronogramaInicio] = useState(null);
    const [cronogramaFin, setCronogramaFin] = useState(null);
    const [clientesOptions, setClientesOptions] = useState([]);
    const [inventario, setInventario] = useState([]); 
    const [inventarioOptions, setInventarioOptions] = useState([]); 
    const [materialSeleccionado, setMaterialSeleccionado] = useState(null);
    const [cantidadAUsar, setCantidadAUsar] = useState(1);
    const [materialesAsignados, setMaterialesAsignados] = useState([]);
    const [activeTab, setActiveTab] = useState('detalles');
    const [historial, setHistorial] = useState([]);
    const [cargandoHistorial, setCargandoHistorial] = useState(true);
    const [nuevaNota, setNuevaNota] = useState('');

    useEffect(() => {
        const unsubClientes = onSnapshot(collection(db, "clientes"), (snapshot) => {
            const clienteOptions = snapshot.docs.map(doc => {
                const data = doc.data();
                const labelCliente = data.name || data.nombre || data.razonSocial || 'Cliente Sin Nombre';
                return { value: doc.id, label: labelCliente };
            });
            setClientesOptions(clienteOptions);

            if (initialData?.clienteId) {
                const clienteEncontrado = clienteOptions.find(opt => opt.value === initialData.clienteId);
                if (clienteEncontrado) setSelectedCliente(clienteEncontrado);
            }
        });

        const unsubMateriaPrima = onSnapshot(collection(db, "materiaPrima"), (snapshot) => {
            const items = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data(), categoria: 'materiaPrima' }));
            setInventario(prev => {
                const soloAccesorios = prev.filter(p => p.categoria === 'accesorios');
                return [...items, ...soloAccesorios];
            });
        });

        const unsubAccesorios = onSnapshot(collection(db, "accesorios"), (snapshot) => {
            const items = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data(), categoria: 'accesorios' }));
            setInventario(prev => {
                const soloMateria = prev.filter(p => p.categoria === 'materiaPrima');
                return [...soloMateria, ...items];
            });
        });

        return () => { unsubClientes(); unsubMateriaPrima(); unsubAccesorios(); };
    }, [initialData]);

    useEffect(() => {
        const options = inventario.map(item => ({ 
            value: item.id, 
            label: `${item.nombre} (Stock: ${item.cantidad})`, 
            categoria: item.categoria, 
            stock: item.cantidad,
            precio: item.precio || 0 
        }));
        setInventarioOptions(options);
    }, [inventario]);
    
    const parseDate = (dateValue) => {
        if (!dateValue) return null;
        if (typeof dateValue.toDate === 'function') return dateValue.toDate();
        if (typeof dateValue === 'string') { const parts = dateValue.split('/'); if (parts.length === 3) return new Date(parts[2], parts[1] - 1, parts[0]); }
        if (dateValue instanceof Date) return dateValue;
        return null;
    };

    useEffect(() => {
        if (initialData) {
            setNombreProyecto(initialData.nombreProyecto || '');
            
            // 🔥 AQUÍ ESTÁ LA CORRECCIÓN CLAVE 🔥
            // Si el dato viene con "Asignado desde...", lo limpiamos. Si no, usamos lo que traiga.
            let vendedorLimpio = initialData.vendedor || '';
            if (vendedorLimpio.includes('Asignado desde')) vendedorLimpio = '';

            setFormData({ 
                ...initialFormState, 
                ...initialData,
                vendedor: vendedorLimpio // Forzamos que esté limpio
            });

            setUbicacion(initialData.ubicacion || '');
            setFechaEntrega(parseDate(initialData.fechaEntrega));
            setCronogramaInicio(parseDate(initialData.cronogramaInicio));
            setCronogramaFin(parseDate(initialData.cronogramaFin));
            setMaterialesAsignados(initialData.materialesAsignados || []);
        } else {
            // Caso: Crear NUEVO proyecto
            setNombreProyecto(''); 
            setFormData(initialFormState); // Aquí ya viene limpio por defecto
            setSelectedCliente(null); 
            setUbicacion('');
            setFechaEntrega(null); 
            setCronogramaInicio(null); 
            setCronogramaFin(null); 
            setMaterialesAsignados([]);
        }
    }, [initialData]);

    useEffect(() => {
        if (initialData?.id && vistaActual) {
            setCargandoHistorial(true);
            const coleccionPadre = `proyectos${vistaActual.charAt(0).toUpperCase() + vistaActual.slice(1)}`;
            const historialRef = collection(db, coleccionPadre, initialData.id, 'historial');
            const q = query(historialRef, orderBy('fecha', 'desc'));

            const unsubscribe = onSnapshot(q, (snapshot) => {
                const notes = snapshot.docs.map(doc => ({ 
                    id: doc.id, ...doc.data(), 
                    fechaFormateada: doc.data().fecha?.toDate().toLocaleString('es-ES', { dateStyle: 'short', timeStyle: 'short' }) 
                }));
                setHistorial(notes); 
                setCargandoHistorial(false);
            }, (error) => {
                console.error("Error en snapshot del historial:", error);
                setCargandoHistorial(false);
                setHistorial([]);
            });
            return () => unsubscribe();
        }
    }, [initialData, vistaActual]);
    
    const handleChange = (e) => setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
    const handleClienteChange = (selectedOption) => setSelectedCliente(selectedOption);

    const handleAsignarMaterial = () => {
        if (!materialSeleccionado || cantidadAUsar <= 0) { return toast.error("Selecciona un material y una cantidad válida."); }
        if (cantidadAUsar > materialSeleccionado.stock) { return toast.error(`Stock insuficiente. Disponible: ${materialSeleccionado.stock}`); }
        
        const materialData = inventario.find(i => i.id === materialSeleccionado.value);
        if (!materialData) return;

        const costeMaterial = (materialData.precio || 0) * cantidadAUsar;
        const materialExistenteIndex = materialesAsignados.findIndex(m => m.itemId === materialSeleccionado.value);
        
        if (materialExistenteIndex > -1) {
            const updatedMateriales = [...materialesAsignados];
            updatedMateriales[materialExistenteIndex].cantidadUsada += cantidadAUsar;
            updatedMateriales[materialExistenteIndex].costoTotal += costeMaterial;
            setMaterialesAsignados(updatedMateriales);
        } else {
            setMaterialesAsignados([...materialesAsignados, {
                itemId: materialSeleccionado.value, 
                nombre: materialData.nombre, 
                cantidadUsada: cantidadAUsar,
                costoUnitario: materialData.precio || 0, 
                costoTotal: costeMaterial, 
                categoria: materialData.categoria
            }]);
        }
        setMaterialSeleccionado(null);
        setCantidadAUsar(1);
    };

    const handleDevolverMaterial = (itemIdToRemove) => {
        setMaterialesAsignados(materialesAsignados.filter(mat => mat.itemId !== itemIdToRemove));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        const costeTotalMateriales = materialesAsignados.reduce((total, mat) => total + (mat.costoTotal || 0), 0);
        const dataToSave = {
            ...formData, 
            nombreProyecto, 
            clienteId: selectedCliente?.value || null, 
            nombreCliente: selectedCliente?.label || '',
            ubicacion, 
            fechaEntrega, 
            cronogramaInicio, 
            cronogramaFin, 
            materialesAsignados, 
            costeTotalMateriales,
        };
        onSave(dataToSave);
    };

    const handleGuardarNota = async () => {
        if (!nuevaNota.trim()) return toast.error("La nota no puede estar vacía.");
        if (!initialData?.id) return toast.error("No se puede guardar una nota sin un proyecto.");

        const coleccionPadre = `proyectos${vistaActual.charAt(0).toUpperCase() + vistaActual.slice(1)}`;
        const historialRef = collection(db, coleccionPadre, initialData.id, 'historial');
        
        const notaAGuardar = {
            texto: nuevaNota,
            fecha: new Date(),
            autor: usuario.displayName || usuario.email || 'Usuario',
            projectName: nombreProyecto,
            clientName: selectedCliente?.label || initialData.nombreCliente || 'Cliente',
        };

        try {
            await toast.promise(addDoc(historialRef, notaAGuardar), {
                loading: 'Guardando nota...', success: '¡Nota guardada!', error: 'Error al guardar la nota.'
            });
            setNuevaNota('');
        } catch (error) {
            console.error("Error al guardar nota:", error);
        }
    };

    const handleDeleteNota = async (notaId) => {
        if (window.confirm("¿Seguro que quieres borrar esta nota?")) {
            const coleccionPadre = `proyectos${vistaActual.charAt(0).toUpperCase() + vistaActual.slice(1)}`;
            const notaRef = doc(db, coleccionPadre, initialData.id, 'historial', notaId);
            try {
                await toast.promise(deleteDoc(notaRef), {
                    loading: 'Borrando nota...', success: '¡Nota borrada!', error: 'Error al borrar la nota.'
                });
            } catch (error) {
                console.error("Error al borrar nota:", error);
            }
        }
    };

    return (
        <form onSubmit={handleSubmit} className="bg-white p-6 sm:p-8 rounded-lg shadow-xl max-h-[90vh] flex flex-col">
            <h3 className="text-xl sm:text-2xl font-bold text-gray-800 mb-6">{initialData ? (isReadOnly ? 'Detalles del Proyecto' : 'Editar Proyecto') : 'Añadir Nuevo Proyecto'}</h3>
            <div className="border-b border-gray-200 mb-6">
                <nav className="-mb-px flex space-x-6">
                    <button type="button" onClick={() => setActiveTab('detalles')} className={`py-3 px-1 border-b-2 font-semibold text-sm ${activeTab === 'detalles' ? 'border-teal-500 text-teal-600' : 'border-transparent text-gray-500'}`}><FaClipboardList className="inline mr-2" />Detalles</button>
                    <button type="button" onClick={() => setActiveTab('historial')} disabled={!initialData} className={`py-3 px-1 border-b-2 font-semibold text-sm ${activeTab === 'historial' ? 'border-teal-500 text-teal-600' : 'border-transparent text-gray-500'} disabled:opacity-50`}><FaBook className="inline mr-2" />Historial</button>
                </nav>
            </div>
            <div className="flex-grow overflow-y-auto pr-4 -mr-4">
                {activeTab === 'detalles' && (
                    <div className="space-y-6">
                        <fieldset disabled={isReadOnly} className="space-y-6">
                               <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                                   <div className="sm:col-span-2"><label className={labelStyle}>Nombre del Proyecto</label><input type="text" value={nombreProyecto} onChange={(e) => setNombreProyecto(e.target.value)} className={inputStyle} required /></div>
                                   <div className="sm:col-span-2"><label className={labelStyle}>Cliente</label><Select styles={selectStyles} options={clientesOptions} value={selectedCliente} onChange={handleClienteChange} isDisabled={isReadOnly} placeholder="Buscar cliente..." /></div>
                                   <div><label className={labelStyle}>Fecha de Entrega</label><DatePicker selected={fechaEntrega} onChange={setFechaEntrega} className={`${inputStyle} w-full`} disabled={isReadOnly} /></div>
                                   <div><label className={labelStyle}>Prioridad</label><select name="prioridad" value={formData.prioridad} onChange={handleChange} className={inputStyle}><option>Normal</option><option>Alta</option><option>Baja</option></select></div>
                                   <div className="sm:col-span-2"><label className={labelStyle}>Ubicación</label><input type="text" value={ubicacion} onChange={(e) => setUbicacion(e.target.value)} className={inputStyle} /></div>
                                   <div><label className={labelStyle}>Contacto</label><input type="text" name="personaContacto" value={formData.personaContacto} onChange={handleChange} className={inputStyle} /></div>
                                   
                                   {/* 🔥 INPUT VENDEDOR: Ahora aparece limpio */}
                                   {usuario?.role !== 'operario' && (
                                       <div>
                                           <label className={labelStyle}>Vendedor</label>
                                           <input 
                                                type="text" 
                                                name="vendedor" 
                                                value={formData.vendedor} 
                                                onChange={handleChange} 
                                                className={inputStyle} 
                                                placeholder="Nombre del Vendedor" 
                                            />
                                       </div>
                                   )}
                                   
                                   <div className="sm:col-span-2"><label className={labelStyle}>Cronograma</label><div className="flex items-center space-x-2"><DatePicker selected={cronogramaInicio} onChange={setCronogramaInicio} className={`${inputStyle} w-full`} disabled={isReadOnly} /><span className="text-gray-500">-</span><DatePicker selected={cronogramaFin} onChange={setCronogramaFin} className={`${inputStyle} w-full`} disabled={isReadOnly}/></div></div>
                                   <div><label className={labelStyle}>Departamento</label><select name="departamento" value={formData.departamento} onChange={handleChange} className={inputStyle}><option>Madera</option><option>Softplay</option><option>Ambas</option></select></div>
                                   <div><label className={labelStyle}>Status</label><select name="estado" value={formData.estado} onChange={handleChange} className={inputStyle}>{STATUS_OPTIONS.map(status => <option key={status}>{status}</option>)}</select></div>
                                   <div className="sm:col-span-2 lg:col-span-4"><label className={labelStyle}>Descripción</label><textarea name="descripcion" value={formData.descripcion} onChange={handleChange} rows="3" className={inputStyle}></textarea></div>
                               </div>
                        </fieldset>
                        <fieldset disabled={isReadOnly} className="pt-6 border-t mt-6">
                                <h3 className="text-xl font-bold text-gray-800 mb-4">Materiales Asignados</h3>
                                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 items-end mb-6">
                                    <div className="lg:col-span-2"><label className={labelStyle}>Seleccionar Material</label><Select options={inventarioOptions} value={materialSeleccionado} onChange={setMaterialSeleccionado} styles={selectStyles} isDisabled={isReadOnly} placeholder="Buscar material..." /></div>
                                    <div><label className={labelStyle}>Cantidad a Usar</label><input type="number" value={cantidadAUsar} onChange={e => setCantidadAUsar(Number(e.target.value))} min="1" className={inputStyle}/></div>
                                </div>
                                <button type="button" onClick={handleAsignarMaterial} disabled={isReadOnly} className="bg-gray-700 text-white font-semibold py-2 px-4 rounded-lg hover:bg-gray-800 disabled:bg-gray-400"><FaPlus className="inline mr-2"/>Asignar</button>
                                <div className="mt-4 space-y-2">
                                    {materialesAsignados.map(mat => (<div key={mat.itemId} className="flex justify-between items-center bg-gray-100 p-2 rounded-md"><p>{mat.nombre} - <span className="font-semibold">{mat.cantidadUsada}</span></p><button type="button" onClick={() => handleDevolverMaterial(mat.itemId)} className="text-red-500 hover:text-red-700 disabled:text-gray-400" disabled={isReadOnly}><FaTrash /></button></div>))}
                                </div>
                        </fieldset>
                    </div>
                )}
                {activeTab === 'historial' && ( 
                    <div className="space-y-6">
                        {!isReadOnly && (
                            <div>
                                <label className={labelStyle}>Añadir Nueva Nota</label>
                                <textarea value={nuevaNota} onChange={(e) => setNuevaNota(e.target.value)} rows="3" className={inputStyle} placeholder="Escribe aquí el seguimiento, una llamada, un acuerdo..."></textarea>
                                <button type="button" onClick={handleGuardarNota} className="mt-2 bg-teal-500 text-white font-semibold py-2 px-4 rounded-lg hover:bg-teal-600 flex items-center gap-2"><FaSave /> Guardar Nota</button>
                            </div>
                        )}
                        <div className="border-t pt-4">
                            <h3 className="text-lg font-bold text-gray-800 mb-4">Notas Anteriores</h3>
                            {cargandoHistorial ? <p>Cargando historial...</p> : 
                                historial.length > 0 ? (
                                    <div className="space-y-4">
                                        {historial.map(nota => (
                                            <div key={nota.id} className="bg-gray-50 p-3 rounded-md border flex justify-between items-start">
                                                <div>
                                                    <p className="text-sm text-gray-800 whitespace-pre-wrap">{nota.texto}</p>
                                                    <p className="text-xs text-gray-500 mt-2">{nota.autor || 'Sistema'} - {nota.fechaFormateada}</p>
                                                </div>
                                                {!isReadOnly && (
                                                    <button type="button" onClick={() => handleDeleteNota(nota.id)} className="text-gray-400 hover:text-red-500 ml-4 p-1" title="Borrar nota">
                                                        <FaTrash />
                                                    </button>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                ) : <p>No hay notas en el historial.</p>
                            }
                        </div>
                    </div> 
                )}
            </div>
            <div className="flex justify-end space-x-4 pt-6 border-t mt-8">
                <button type="button" onClick={onCancel} className="bg-gray-200 text-gray-800 font-bold h-11 px-6 rounded-lg hover:bg-gray-300">Cancelar</button>
                {!isReadOnly && ( <button type="submit" className="bg-[#18BC9C] text-white font-bold h-11 px-6 rounded-lg shadow-md hover:bg-[#16a085]">Guardar Proyecto</button> )}
            </div>
        </form>
    );
}

export default ProjectForm;